import { get, isEmpty, isNil } from 'lodash';
import { createSelector } from 'reselect';
import { CountryCode, CUSTOMER_SEGMENT_ENUM } from 'src/settings/settings';
import { ApplicationState } from '../../types';

export const getPersonalInformation = ({
  personalInformation,
}: ApplicationState) => personalInformation;

export const getPersonalInformationData = createSelector(
  [getPersonalInformation],
  ({ data }) => data
);

export const didPersonalInformationFail = createSelector(
  [getPersonalInformation],
  ({ hasFailed }) => hasFailed
);

export const getUserAge = createSelector(
  [getPersonalInformationData],
  (data) => data?.age
);

/**
 * Simple selector to return the complex object which contains entity information
 * @param state
 * @returns {EntitySegment | Object}
 *
 */
export const getUserEntitySegment = createSelector(
  [getPersonalInformationData],
  (data) => data?.entitySegment
);

/**
 * Simple selector to the number associated to that users client type that matches the client enumeration
 * in the entity segment object
 * @param getUserEntitySegment
 * @returns {Number}
 *
 */
export const getCustomerEntitySegmentId = createSelector(
  [getUserEntitySegment],
  (entitySegment) => entitySegment?.enumerationID
);

/**
 * Simple selector to the name associated to that users client type that matches the client enumeration
 * in the entity segment object
 * @param getUserEntitySegment
 * @returns {String}
 * Ex. "VIP"
 *
 */
export const getCustomerSegmentName = createSelector(
  [getCustomerEntitySegmentId],
  (customerEntitySegmentId = '') => {
    if (customerEntitySegmentId in CUSTOMER_SEGMENT_ENUM) {
      return CUSTOMER_SEGMENT_ENUM[customerEntitySegmentId];
    }
  }
);

export const getUserAddress = createSelector(
  getPersonalInformationData,
  (personalInformation) => personalInformation?.primaryAddress
);

/**
 * Simple selector to retrieve businessPartyId from user entity
 * @param getPersonalInformationData
 * @returns {Number} businessPartyId
 */
export const getUserBusinessPartyId = createSelector(
  [getPersonalInformationData],
  (personalInformationData) => personalInformationData?.businessPartyId
);

/**
 * Simple selector to check user has an email in personal info data
 * @param getPersonalInformationData
 * @returns {Boolean}
 */
export const getHasUserEmail = createSelector(
  [getPersonalInformationData],
  (personalInformation) =>
    !isNil(personalInformation?.emailAddress?.address) &&
    personalInformation?.emailAddress?.address?.length > 0
);

export const getHasServiceAccountFee = createSelector(
  [getPersonalInformationData],
  (personalInformation) =>
    isNil(personalInformation?.hasAccountServiceFee)
      ? false
      : personalInformation?.hasAccountServiceFee
);

export const getUserFirstName = createSelector(
  [getPersonalInformationData],
  (personalInformation) =>
    isNil(personalInformation?.firstName) ? '' : personalInformation?.firstName
);
export const getDateOfBirth = createSelector(
  [getPersonalInformationData],
  (personalInformation) => personalInformation?.dateOfBirth
);

export const isDateOfBirthAvailable = createSelector(
  [getPersonalInformationData],
  (personalInformation) =>
    !isNil(personalInformation?.dateOfBirth) &&
    personalInformation?.dateOfBirth !== 0
);

export const getIsAddressAvailable = createSelector(
  [getUserAddress],
  (userAddress) => {
    const street1 = get(userAddress, 'street1');
    const city = get(userAddress, 'city');
    const state = get(userAddress, 'state');
    const zip = get(userAddress, 'zip');
    const countryCode = get(userAddress, 'countryCode', '');
    const isStateOptional = countryCode !== 'US' && countryCode in CountryCode;
    return (
      !isEmpty(street1) &&
      !isEmpty(city) &&
      (isStateOptional || !isEmpty(state)) &&
      !isEmpty(zip) &&
      !isEmpty(countryCode)
    );
  }
);

export const getIsNotForeignAddress = createSelector(
  [getUserAddress],
  (userAddress) => {
    const countryCode = get(userAddress, 'countryCode', '');
    return countryCode in CountryCode;
  }
);

export const getUserState = createSelector([getUserAddress], (userAddress) =>
  get(userAddress, 'state')
);

export const getUserMessageCount = createSelector(
  [getPersonalInformation],
  (personalInfo) => {
    return personalInfo.messagesNumber;
  }
);

// To Be Used to pull household info in entity/details call instead of nonexistent household call
export const getHouseholdBalanceTierStatus = createSelector(
  [getPersonalInformationData],
  (personalInformation) => {
    const household = get(personalInformation, 'household');
    return household && household.householdBalanceTierStatus;
  }
);

export const getUserBPID = createSelector(
  [getPersonalInformationData],
  (personalInfoData) => {
    return personalInfoData?.businessPartyId;
  }
);
